import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';

function formatDateTime(isoString) {
  if (!isoString) return '';
  const dateObj = new Date(isoString);
  const datePart = dateObj.toLocaleDateString('de-DE');
  const timePart = dateObj.toLocaleTimeString('de-DE', {
    hour: '2-digit',
    minute: '2-digit'
  });
  return `${datePart} ${timePart}`;
}

function buildRyanairUrl(fromIata, toIata, outboundDate, returnDate) {
  if (!fromIata || !toIata || !outboundDate) {
    return 'https://www.ryanair.com/de/de';
  }
  const dateOut = new Date(outboundDate).toISOString().split('T')[0];
  const dateIn = returnDate
    ? new Date(returnDate).toISOString().split('T')[0]
    : dateOut;

  let url = 'https://www.ryanair.com/de/de/trip/flights/select';
  url += '?adults=1&teens=0&children=0&infants=0';
  url += `&dateOut=${dateOut}&dateIn=${dateIn}`;
  url += '&isConnectedFlight=false&discount=0&promoCode=&isReturn=true';
  url += `&originIata=${fromIata}&destinationIata=${toIata}`;
  url += `&tpAdults=1&tpTeens=0&tpChildren=0&tpInfants=0`;
  url += `&tpStartDate=${dateOut}&tpEndDate=${dateIn}`;
  url += `&tpDiscount=0&tpPromoCode=`;
  url += `&tpOriginIata=${fromIata}&tpDestinationIata=${toIata}`;
  return url;
}

function App() {
  const [departureCities, setDepartureCities] = useState([]);
  const [destinationCities, setDestinationCities] = useState([]);

  const [selectedFrom, setSelectedFrom] = useState('');
  const [selectedTo, setSelectedTo] = useState('');

  const [trips, setTrips] = useState([]);

  const [filters, setFilters] = useState({
    maxPrice: '',
    minDays: '',
    maxDays: '',
    month: ''
  });

  const [sort, setSort] = useState({ sortBy: '', sortOrder: 'ASC' });

  useEffect(() => {
    axios.get('/api/cities?direction=from')
      .then(response => setDepartureCities(response.data))
      .catch(err => console.error(err));
  }, []);

  useEffect(() => {
    if (selectedFrom) {
      axios.get(`/api/destinations?from=${selectedFrom}`)
        .then(response => setDestinationCities(response.data))
        .catch(err => console.error(err));
    } else {
      setDestinationCities([]);
    }
  }, [selectedFrom]);

  useEffect(() => {
    if (selectedFrom && selectedTo) {
      axios.get('/api/trips', {
        params: {
          from: selectedFrom,
          to: selectedTo,
          maxPrice: filters.maxPrice || undefined,
          minDays: filters.minDays || undefined,
          maxDays: filters.maxDays || undefined,
          month: filters.month || undefined,
          sortBy: sort.sortBy || undefined,
          sortOrder: sort.sortOrder || undefined
        }
      })
      .then(response => {
        const transformed = response.data.map(trip => ({
          id: trip.id,
          outboundDeparture: trip.outbounddeparture,
          returnDeparture: trip.returndeparture,
          totalPrice: trip.totalprice,
          durationDays: trip.durationdays,
          lastUpdate: trip.lastupdate
        }));
        setTrips(transformed);
      })
      .catch(err => console.error(err));
    } else {
      setTrips([]);
    }
  }, [selectedFrom, selectedTo, filters, sort]);

  const handleSort = (column) => {
    setSort(prev => ({
      sortBy: column,
      sortOrder: prev.sortOrder === 'ASC' ? 'DESC' : 'ASC'
    }));
  };

  return (
    <div className="container">
      <h1 className="app-title">Ryanair Billigflüge Datenbank</h1>

      <div className="selectors">
        <div className="selector-block">
          <label>Abfahrt: </label>
          <select value={selectedFrom} onChange={(e) => setSelectedFrom(e.target.value)}>
            <option value="">Stadt wählen</option>
            {departureCities.map(city => (
              <option key={city.id} value={city.iata_code}>
                {city.city_name}
              </option>
            ))}
          </select>
        </div>

        {selectedFrom && (
          <div className="selector-block">
            <label>Zielort: </label>
            <select value={selectedTo} onChange={(e) => setSelectedTo(e.target.value)}>
              <option value="">Stadt wählen</option>
              {destinationCities.map(city => (
                <option key={city.id} value={city.iata_code}>
                  {city.city_name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedFrom && selectedTo && (
        <div className="filters">
          <h3>Filtern</h3>

          <div className="filter-row">
            <label>Max. Preis: </label>
            <input type="number" value={filters.maxPrice}
              onChange={(e) => setFilters({ ...filters, maxPrice: e.target.value })} />
          </div>

          <div className="filter-row">
            <label>Min. Dauer (Tage): </label>
            <input type="number" value={filters.minDays}
              onChange={(e) => setFilters({ ...filters, minDays: e.target.value })} />
          </div>

          <div className="filter-row">
            <label>Max. Dauer (Tage): </label>
            <input type="number" value={filters.maxDays}
              onChange={(e) => setFilters({ ...filters, maxDays: e.target.value })} />
          </div>

          <div className="filter-row">
            <label>Monat:</label>
            <select value={filters.month}
              onChange={(e) => setFilters({ ...filters, month: e.target.value })}>
              <option value="">Alle Monate</option>
              <option value="1">Januar</option>
              <option value="2">Februar</option>
              <option value="3">März</option>
              <option value="4">April</option>
              <option value="5">Mai</option>
              <option value="6">Juni</option>
              <option value="7">Juli</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">Oktober</option>
              <option value="11">November</option>
              <option value="12">Dezember</option>
            </select>
          </div>
        </div>
      )}

      {selectedFrom && selectedTo && trips.length > 0 && (
        <div className="results-table">
          <h3>Reisen</h3>
          <table>
            <thead>
              <tr>
                <th onClick={() => handleSort('outboundDeparture')}>Datum des Abfluges</th>
                <th onClick={() => handleSort('returnDeparture')}>Datum der Rückreise</th>
                <th onClick={() => handleSort('totalPrice')}>Gesamtpreis</th>
                <th onClick={() => handleSort('durationDays')}>Dauer (Tage)</th>
                <th onClick={() => handleSort('lastUpdate')}>Aktualisiert am</th>
                <th>Link</th>
              </tr>
            </thead>
            <tbody>
              {trips.map(trip => (
                <tr key={trip.id}>
                  <td>{formatDateTime(trip.outboundDeparture)}</td>
                  <td>{formatDateTime(trip.returnDeparture)}</td>
                  <td>{trip.totalPrice}</td>
                  <td>{trip.durationDays}</td>
                  <td>{formatDateTime(trip.lastUpdate)}</td>
                  <td>
                    <a
                      href={buildRyanairUrl(
                        selectedFrom,
                        selectedTo,
                        trip.outboundDeparture,
                        trip.returnDeparture
                      )}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Kaufen
                    </a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {selectedFrom && selectedTo && trips.length === 0 && (
        <p className="no-trips">
          Keine Fahrten für das ausgewählte Städtepaar (oder Filter) verfügbar.
        </p>
      )}
    </div>
  );
}

export default App;
